import React, {createContext, useContext, useReducer, useTransition, type Dispatch, type PropsWithChildren} from 'react';

export type MODAL_VIEWS =
    | 'ENTRANCE_POPUP'
    | 'REGISTER'
    | 'LOGIN_VIEW'
    | 'CART'
    | 'FORGOT_VIEW'
    | 'OTP_LOGIN'
    | 'ADD_OR_UPDATE_ADDRESS'
    | 'ADD_OR_UPDATE_CHECKOUT_CONTACT'
    | 'ADD_OR_UPDATE_PROFILE_CONTACT'
    | 'ADD_OR_UPDATE_GUEST_ADDRESS'
    | 'DELETE_ADDRESS'
    | 'PRODUCT_DETAILS'
    | 'REFUND_REQUEST'
    | 'REVIEW_RATING'
    | 'QUESTION_FORM'
    | 'ABUSE_REPORT'
    | 'SHOP_INFO'
    | 'SELECT_PRODUCT_VARIATION'
    | 'REVIEW_IMAGE_POPOVER'
    | 'USE_NEW_PAYMENT'
    | 'PAYMENT_MODAL'
    | 'DELETE_CARD_MODAL'
    | 'ADD_NEW_CARD'
    | 'ADD_NEW_CARD_DURING_PAYMENT'
    | 'STORE_SWITCHER'
    | 'USE_SAVED_CARD'
    | 'UPDATE_PASSWORD_VIEW'
    | 'UPDATE_EMAIL_VIEW'
    | 'BUY_AGAIN_VIEW'
    | 'REVIEW_IMAGES_GALLERY'
    | 'SHIPPING_ADDRESS_NOT_MATCH_STORE_VIEW';

/** Auth-related modal views that should be locked after successful authentication */
const AUTH_MODAL_VIEWS: MODAL_VIEWS[] = ['LOGIN_VIEW', 'OTP_LOGIN', 'REGISTER'];

/** Store switcher modal view locked after successful store change */
const STORE_MODAL_VIEWS: MODAL_VIEWS[] = ['STORE_SWITCHER'];

/**
 * Module-level lock timestamps for modal view groups.
 * After a successful action, related modals are blocked from opening
 * for a brief period to prevent flashing during page transitions.
 */
let authModalLockedUntil = 0;
let storeModalLockedUntil = 0;

/** Check if a view is currently locked */
function isViewLocked(view: MODAL_VIEWS): boolean {
    return (
        (AUTH_MODAL_VIEWS.includes(view) && Date.now() < authModalLockedUntil) ||
        (STORE_MODAL_VIEWS.includes(view) && Date.now() < storeModalLockedUntil)
    );
}

interface State {
    view?: MODAL_VIEWS;
    data?: any;
    isOpen: boolean;
}
type Action = {type: 'open'; view?: MODAL_VIEWS; payload?: any} | {type: 'close'};

const initialState: State = {
    view: undefined,
    isOpen: false,
    data: null,
};

function modalReducer(state: State, action: Action): State {
    switch (action.type) {
        case 'open':
            return {
                ...state,
                view: action.view,
                data: action.payload,
                isOpen: true,
            };
        case 'close':
            return {
                ...state,
                view: undefined,
                data: null,
                isOpen: false,
            };
        default:
            throw new Error('Unknown Modal Action!');
    }
}

const ModalStateContext = createContext<State>(initialState);
ModalStateContext.displayName = 'ModalStateContext';
const ModalActionContext = createContext<Dispatch<Action> | undefined>(undefined);
ModalActionContext.displayName = 'ModalActionContext';

export const ModalProvider = ({children}: PropsWithChildren<{}>) => {
    const [state, dispatch] = useReducer(modalReducer, initialState);
    return (
        <ModalStateContext.Provider value={state}>
            <ModalActionContext.Provider value={dispatch}>{children}</ModalActionContext.Provider>
        </ModalStateContext.Provider>
    );
};

export function useModalState() {
    const context = useContext(ModalStateContext);
    if (context === undefined) {
        throw new Error(`useModalState must be used within a ModalProvider`);
    }
    return context;
}

export function useModalAction() {
    const dispatch = useContext(ModalActionContext);
    const [, startTransition] = useTransition();
    if (dispatch === undefined) {
        throw new Error(`useModalAction must be used within a ModalProvider`);
    }
    return {
        openModal(view?: MODAL_VIEWS, payload?: unknown) {
            // Block locked modal views from opening during post-action lock period
            if (view && isViewLocked(view)) {
                return;
            }
            startTransition(() => dispatch({type: 'open', view, payload}));
        },
        closeModal() {
            startTransition(() => dispatch({type: 'close'}));
        },
        /**
         * Lock auth-related modals from opening for a specified duration.
         * Used after successful authentication to prevent the modal from
         * flashing open during router.refresh() re-renders.
         */
        lockAuthModals(durationMs = 5000) {
            authModalLockedUntil = Date.now() + durationMs;
        },
        /**
         * Lock store switcher modal from opening for a specified duration.
         * Used after successful store change to prevent the modal from
         * flashing open during window.location.reload().
         */
        lockStoreModals(durationMs = 5000) {
            storeModalLockedUntil = Date.now() + durationMs;
        },
    };
}
